/**
 * @file pages/ClientPortalPage.js
 * @description Public-facing client portal for viewing inspection reports.
 * Token-based access — no login required. Clients receive a link and can
 * view their report, browse photos, read the summary, sign, and download PDF.
 *
 * Apple-clean design. Mobile-first. Professional.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";

// ── API helpers ────────────────────────────────────────────────────
const API_BASE = "/api/portal";

async function fetchPortalData(token) {
  const res = await fetch(`${API_BASE}/${token}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || "Failed to load report");
  return json.data;
}

async function submitSignature(token, signatureData, signedByName) {
  const res = await fetch(`${API_BASE}/${token}/sign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ signatureData, signedByName })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || "Failed to submit signature");
  return json.data;
}

async function requestCopy(token, email) {
  const res = await fetch(`${API_BASE}/${token}/request-copy`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email })
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || "Failed to send copy");
  return json.data;
}

// ── Date formatter ─────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric"
  });
}

// ── Severity / Status configs ──────────────────────────────────────
const SEVERITY_CONFIG = {
  safety:   { color: "#C0392B", label: "SAFETY",   bg: "#FADBD8" },
  major:    { color: "#E74C3C", label: "MAJOR",    bg: "#FDEDEC" },
  moderate: { color: "#E67E22", label: "MODERATE", bg: "#FEF5E7" },
  minor:    { color: "#F39C12", label: "MINOR",    bg: "#FEF9E7" },
  info:     { color: "#3498DB", label: "INFO",     bg: "#EBF5FB" }
};

const STATUS_CONFIG = {
  pass:  { color: "#27AE60", label: "PASS",  icon: "\u2713" },
  fail:  { color: "#E74C3C", label: "FAIL",  icon: "\u2717" },
  "n/a": { color: "#95A5A6", label: "N/A",   icon: "\u2014" }
};

// ── Main Component ─────────────────────────────────────────────────
function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("report");
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchPortalData(token)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} />;
  if (!data) return <ErrorScreen message="No data available" />;

  const { inspection, photos, company, inspectorName } = data;
  const primaryColor = company.primaryColor || "#1B4F72";
  const companyName = company.name || "Inspection Butler";

  return (
    <div style={styles.pageWrapper}>
      {/* Header */}
      <header style={{ ...styles.header, borderBottomColor: primaryColor }}>
        <div style={styles.headerInner}>
          <div style={styles.headerLeft}>
            {company.logoUrl && (
              <img src={company.logoUrl} alt={companyName} style={styles.logo} />
            )}
            <div>
              <h1 style={{ ...styles.companyName, color: primaryColor }}>{companyName}</h1>
              <p style={styles.headerSubtitle}>Inspection Report</p>
            </div>
          </div>
          <a
            href={`${API_BASE}/${token}/pdf`}
            style={{ ...styles.downloadBtn, background: primaryColor }}
            download
          >
            <span style={styles.downloadIcon}>{"\u2193"}</span>
            <span style={styles.downloadText}>Download PDF</span>
          </a>
        </div>
      </header>

      {/* Property Info Banner */}
      <div style={styles.propertyBanner}>
        <h2 style={styles.propertyAddress}>{inspection.propertyAddress || "Property Address"}</h2>
        <p style={styles.propertyCityLine}>
          {[inspection.propertyCity, inspection.propertyState].filter(Boolean).join(", ")}
          {inspection.propertyZip ? " " + inspection.propertyZip : ""}
        </p>
        <div style={styles.infoRow}>
          <div style={styles.infoChip}>
            <span style={styles.infoLabel}>Date</span>
            <span style={styles.infoValue}>{formatDate(inspection.scheduledDate || inspection.completedAt)}</span>
          </div>
          <div style={styles.infoChip}>
            <span style={styles.infoLabel}>Inspector</span>
            <span style={styles.infoValue}>{inspectorName}</span>
          </div>
          <div style={styles.infoChip}>
            <span style={styles.infoLabel}>Report #</span>
            <span style={styles.infoValue}>{inspection.inspectionNumber}</span>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <nav style={styles.tabBar}>
        <div style={styles.tabBarInner}>
          {["report", "photos", "summary", "sign"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                ...styles.tab,
                ...(activeTab === tab ? { ...styles.tabActive, borderBottomColor: primaryColor, color: primaryColor } : {})
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </nav>

      {/* Tab Content */}
      <main style={styles.mainContent}>
        {activeTab === "report" && (
          <ReportTab inspection={inspection} photos={photos} primaryColor={primaryColor} onPhotoClick={setLightboxPhoto} />
        )}
        {activeTab === "photos" && (
          <PhotosTab inspection={inspection} photos={photos} onPhotoClick={setLightboxPhoto} />
        )}
        {activeTab === "summary" && (
          <SummaryTab inspection={inspection} primaryColor={primaryColor} />
        )}
        {activeTab === "sign" && (
          <SignTab inspection={inspection} token={token} primaryColor={primaryColor} onSigned={(s) => {
            setData(prev => ({
              ...prev,
              inspection: {
                ...prev.inspection,
                clientSignature: s.signatureData,
                clientSignedAt: s.clientSignedAt,
                clientSignedName: s.clientSignedName
              }
            }));
          }} />
        )}
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          {(company.phone || company.email) && (
            <p style={styles.footerContact}>
              {company.phone && <span>{company.phone}</span>}
              {company.phone && company.email && <span style={styles.footerDivider}> | </span>}
              {company.email && <a href={`mailto:${company.email}`} style={styles.footerLink}>{company.email}</a>}
            </p>
          )}
          <p style={styles.footerDisclaimer}>
            This inspection report is based on a visual examination of the readily accessible areas of the property at the time of the inspection.
            It is not a guarantee or warranty of any kind.
          </p>
          <p style={styles.footerBrand}>Powered by Inspection Butler</p>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxPhoto && (
        <Lightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />
      )}
    </div>
  );
}

// ── Report Tab ─────────────────────────────────────────────────────
function ReportTab({ inspection, photos, primaryColor, onPhotoClick }) {
  const responses = inspection.responses || [];
  const sections = {};
  responses.forEach(r => {
    const sn = r.sectionName || "General";
    if (!sections[sn]) sections[sn] = [];
    sections[sn].push(r);
  });
  const sectionNames = Object.keys(sections);

  // Photo map by responseId
  const photoMap = {};
  photos.forEach(p => {
    const rid = p.responseId || "_general";
    if (!photoMap[rid]) photoMap[rid] = [];
    photoMap[rid].push(p);
  });

  return (
    <div>
      {sectionNames.map(sectionName => (
        <CollapsibleSection key={sectionName} title={sectionName} primaryColor={primaryColor}>
          {sections[sectionName].map((item, idx) => {
            const statusCfg = STATUS_CONFIG[item.value] || { color: "#95A5A6", label: item.value || "-", icon: "" };
            const isDeficient = item.isDeficiency;
            const sevCfg = isDeficient ? (SEVERITY_CONFIG[item.deficiencySeverity] || SEVERITY_CONFIG.info) : null;
            const itemPhotos = photoMap[item.id] || [];

            return (
              <div key={item.id || idx} style={{
                ...styles.itemCard,
                borderLeft: isDeficient ? `3px solid ${sevCfg.color}` : "3px solid transparent",
                background: isDeficient ? sevCfg.bg : (idx % 2 === 0 ? "#F8F9FA" : "#fff")
              }}>
                <div style={styles.itemHeader}>
                  <span style={{ ...styles.itemName, fontWeight: isDeficient ? 600 : 400 }}>
                    {item.itemLabel || "Item"}
                  </span>
                  <span style={{ ...styles.badge, background: statusCfg.color }}>
                    {statusCfg.icon} {statusCfg.label}
                  </span>
                  {isDeficient && (
                    <span style={{ ...styles.badge, background: sevCfg.color, marginLeft: 4 }}>
                      {sevCfg.label}
                    </span>
                  )}
                </div>
                {item.notes && <p style={styles.itemNotes}>{item.notes}</p>}
                {itemPhotos.length > 0 && (
                  <div style={styles.photoGrid}>
                    {itemPhotos.map(p => (
                      <div key={p.id} style={styles.photoThumb} onClick={() => onPhotoClick(p)}>
                        <img src={p.thumbnailPath || p.url} alt={p.caption || ""} style={styles.photoThumbImg} loading="lazy" />
                        {p.caption && <div style={styles.photoCaption}>{p.caption}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      ))}
    </div>
  );
}

// ── Collapsible Section ────────────────────────────────────────────
function CollapsibleSection({ title, primaryColor, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={styles.section}>
      <button onClick={() => setOpen(!open)} style={{ ...styles.sectionTitle, borderBottomColor: primaryColor, color: primaryColor }}>
        <span>{title}</span>
        <span style={{ ...styles.collapseIcon, transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>{"\u25BC"}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

// ── Photos Tab ─────────────────────────────────────────────────────
function PhotosTab({ inspection, photos, onPhotoClick }) {
  const responses = inspection.responses || [];
  // Group photos by section
  const responseMap = {};
  responses.forEach(r => { responseMap[r.id] = r; });

  const sectionPhotos = {};
  const generalPhotos = [];

  photos.forEach(p => {
    if (p.responseId && responseMap[p.responseId]) {
      const sn = responseMap[p.responseId].sectionName || "General";
      if (!sectionPhotos[sn]) sectionPhotos[sn] = [];
      sectionPhotos[sn].push(p);
    } else {
      generalPhotos.push(p);
    }
  });

  if (photos.length === 0) {
    return <div style={styles.emptyState}>No photos were taken during this inspection.</div>;
  }

  return (
    <div>
      {Object.keys(sectionPhotos).map(sn => (
        <div key={sn} style={{ marginBottom: 32 }}>
          <h3 style={styles.photoSectionTitle}>{sn}</h3>
          <div style={styles.galleryGrid}>
            {sectionPhotos[sn].map(p => (
              <div key={p.id} style={styles.galleryItem} onClick={() => onPhotoClick(p)}>
                <img src={p.thumbnailPath || p.url} alt={p.caption || ""} style={styles.galleryImg} loading="lazy" />
                {p.caption && <div style={styles.galleryCaption}>{p.caption}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {generalPhotos.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={styles.photoSectionTitle}>General</h3>
          <div style={styles.galleryGrid}>
            {generalPhotos.map(p => (
              <div key={p.id} style={styles.galleryItem} onClick={() => onPhotoClick(p)}>
                <img src={p.thumbnailPath || p.url} alt={p.caption || ""} style={styles.galleryImg} loading="lazy" />
                {p.caption && <div style={styles.galleryCaption}>{p.caption}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary Tab ────────────────────────────────────────────────────
function SummaryTab({ inspection, primaryColor }) {
  const responses = inspection.responses || [];
  const totalItems = responses.length;
  const passed = responses.filter(r => r.value === "pass").length;
  const failed = responses.filter(r => r.value === "fail").length;
  const naCount = responses.filter(r => r.value === "n/a").length;
  const deficiencies = responses.filter(r => r.isDeficiency);

  const severityCounts = {};
  deficiencies.forEach(d => {
    const sev = d.deficiencySeverity || "info";
    severityCounts[sev] = (severityCounts[sev] || 0) + 1;
  });

  const passRate = totalItems > 0 ? Math.round((passed / totalItems) * 100) : 0;

  let overallCondition = "Good";
  let conditionColor = "#27AE60";
  if (severityCounts.safety > 0) { overallCondition = "Safety Concerns Present"; conditionColor = "#C0392B"; }
  else if (severityCounts.major > 0) { overallCondition = "Major Issues Found"; conditionColor = "#E74C3C"; }
  else if (deficiencies.length > 3) { overallCondition = "Fair \u2014 Multiple Issues"; conditionColor = "#F39C12"; }
  else if (deficiencies.length > 0) { overallCondition = "Good \u2014 Minor Issues"; conditionColor = "#F39C12"; }

  // Sort deficiencies by severity
  const sevOrder = { safety: 0, major: 1, moderate: 2, minor: 3, info: 4 };
  const sortedDeficiencies = [...deficiencies].sort((a, b) =>
    (sevOrder[a.deficiencySeverity] || 99) - (sevOrder[b.deficiencySeverity] || 99)
  );

  return (
    <div>
      {/* Overall condition banner */}
      <div style={{ ...styles.conditionBanner, background: conditionColor }}>
        <div style={styles.conditionLabel}>Overall Condition</div>
        <div style={styles.conditionValue}>{overallCondition}</div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <StatCard label="Total Items" value={totalItems} color={primaryColor} />
        <StatCard label="Passed" value={passed} color="#27AE60" />
        <StatCard label="Failed" value={failed} color="#E74C3C" />
        <StatCard label="N/A" value={naCount} color="#95A5A6" />
      </div>

      {/* Pass rate */}
      <div style={styles.passRateSection}>
        <div style={styles.passRateLabel}>Pass Rate: {passRate}%</div>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: passRate + "%", background: "#27AE60" }} />
        </div>
      </div>

      {/* Severity breakdown */}
      {deficiencies.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={styles.summarySubtitle}>Deficiencies by Severity</h3>
          {Object.keys(SEVERITY_CONFIG).map(key => {
            if (!severityCounts[key]) return null;
            const cfg = SEVERITY_CONFIG[key];
            const pct = Math.round((severityCounts[key] / deficiencies.length) * 100);
            return (
              <div key={key} style={styles.severityRow}>
                <span style={{ ...styles.sevDot, background: cfg.color }} />
                <span style={{ ...styles.sevLabel, color: cfg.color }}>{cfg.label}</span>
                <span style={styles.sevCount}>{severityCounts[key]}</span>
                <div style={styles.sevBar}>
                  <div style={{ ...styles.sevBarFill, width: pct + "%", background: cfg.color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inspector summary */}
      {inspection.summary && (
        <div style={{ marginTop: 32 }}>
          <h3 style={styles.summarySubtitle}>Inspector Summary</h3>
          <p style={styles.summaryText}>{inspection.summary}</p>
        </div>
      )}

      {/* Deficiency list */}
      {sortedDeficiencies.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={styles.summarySubtitle}>Deficiency Details</h3>
          {sortedDeficiencies.map((d, i) => {
            const sevCfg = SEVERITY_CONFIG[d.deficiencySeverity] || SEVERITY_CONFIG.info;
            return (
              <div key={d.id || i} style={{ ...styles.deficiencyCard, borderLeftColor: sevCfg.color, background: sevCfg.bg }}>
                <div style={styles.defHeader}>
                  <span style={styles.defName}>{i + 1}. {d.itemLabel || "Item"}</span>
                  <span style={{ ...styles.badge, background: sevCfg.color }}>{sevCfg.label}</span>
                </div>
                <div style={styles.defSection}>Section: {d.sectionName || "General"}</div>
                {d.notes && <div style={styles.defNotes}>{d.notes}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statValue, color }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

// ── Sign Tab ───────────────────────────────────────────────────────
function SignTab({ inspection, token, primaryColor, onSigned }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const lastPos = useRef(null);

  // Already signed
  if (inspection.clientSignedAt) {
    return (
      <div style={styles.signedContainer}>
        <div style={styles.signedCheck}>{"\u2713"}</div>
        <h3 style={styles.signedTitle}>Report Signed</h3>
        <p style={styles.signedInfo}>
          Signed by <strong>{inspection.clientSignedName}</strong> on{" "}
          {formatDate(inspection.clientSignedAt)}
        </p>
        {inspection.clientSignature && (
          <img src={inspection.clientSignature} alt="Client Signature" style={styles.signaturePreview} />
        )}
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.signedContainer}>
        <div style={styles.signedCheck}>{"\u2713"}</div>
        <h3 style={styles.signedTitle}>Thank You!</h3>
        <p style={styles.signedInfo}>Your signature has been recorded successfully.</p>
      </div>
    );
  }

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => { e.preventDefault(); setIsDrawing(true); lastPos.current = getPos(e); };
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1d1d1f";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  };
  const stopDrawing = () => { setIsDrawing(false); lastPos.current = null; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const handleSubmit = async () => {
    if (isEmpty) { setErrorMsg("Please draw your signature"); return; }
    if (!name.trim()) { setErrorMsg("Please type your name"); return; }
    setErrorMsg("");
    setSubmitting(true);
    try {
      const sigData = canvasRef.current.toDataURL("image/png");
      const result = await submitSignature(token, sigData, name.trim());
      setSuccess(true);
      if (onSigned) onSigned({ signatureData: sigData, clientSignedAt: result.clientSignedAt, clientSignedName: result.clientSignedName });
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.signSection}>
      <h3 style={styles.signTitle}>Sign This Report</h3>
      <p style={styles.signSubtitle}>
        By signing below, you acknowledge receipt of this inspection report.
      </p>

      <div style={styles.signField}>
        <label style={styles.signLabel}>Your Full Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Type your full name"
          style={styles.signInput}
          maxLength={200}
        />
      </div>

      <div style={styles.signField}>
        <label style={styles.signLabel}>Your Signature</label>
        <div style={styles.canvasWrapper}>
          <canvas
            ref={canvasRef}
            width={600}
            height={150}
            style={styles.canvas}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {isEmpty && <div style={styles.canvasPlaceholder}>Sign here</div>}
        </div>
        <button onClick={clearCanvas} style={styles.clearBtn} type="button">Clear</button>
      </div>

      {errorMsg && <p style={styles.errorText}>{errorMsg}</p>}

      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{ ...styles.submitBtn, background: primaryColor, opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? "Submitting..." : "Submit Signature"}
      </button>
    </div>
  );
}

// ── Lightbox ───────────────────────────────────────────────────────
function Lightbox({ photo, onClose }) {
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      style={styles.lightbox}
      onClick={onClose}
      onTouchStart={e => setTouchStart(e.touches[0].clientY)}
      onTouchEnd={e => {
        if (touchStart !== null) {
          const diff = e.changedTouches[0].clientY - touchStart;
          if (Math.abs(diff) > 100) onClose();
        }
        setTouchStart(null);
      }}
    >
      <button style={styles.lightboxClose} onClick={onClose}>{"\u2715"}</button>
      <img
        src={photo.annotatedPath || photo.url}
        alt={photo.caption || ""}
        style={styles.lightboxImg}
        onClick={e => e.stopPropagation()}
      />
      {photo.caption && <div style={styles.lightboxCaption}>{photo.caption}</div>}
    </div>
  );
}

// ── Loading / Error screens ────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={styles.screenCenter}>
      <div style={styles.spinner} />
      <p style={{ color: "#7F8C8D", marginTop: 16, fontSize: 15 }}>Loading your inspection report...</p>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={styles.screenCenter}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{"\u26A0"}</div>
      <h2 style={{ color: "#2C3E50", marginBottom: 8, fontSize: 20 }}>Unable to Load Report</h2>
      <p style={{ color: "#7F8C8D", fontSize: 15, maxWidth: 400, textAlign: "center", lineHeight: 1.5 }}>{message}</p>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────
const styles = {
  pageWrapper: {
    minHeight: "100vh",
    background: "#F0F2F5",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#2C3E50",
    WebkitFontSmoothing: "antialiased"
  },

  // Header
  header: {
    background: "#fff",
    borderBottom: "3px solid #1B4F72",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
  },
  headerInner: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  logo: { height: 40, width: "auto", borderRadius: 6 },
  companyName: { fontSize: 18, fontWeight: 700, margin: 0, lineHeight: 1.2 },
  headerSubtitle: { fontSize: 12, color: "#7F8C8D", margin: 0 },
  downloadBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 18px",
    color: "#fff",
    borderRadius: 8,
    textDecoration: "none",
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: "nowrap",
    border: "none",
    cursor: "pointer"
  },
  downloadIcon: { fontSize: 16, fontWeight: 700 },
  downloadText: {},

  // Property banner
  propertyBanner: {
    background: "#fff",
    padding: "28px 20px 20px",
    textAlign: "center",
    borderBottom: "1px solid #DEE2E6"
  },
  propertyAddress: { fontSize: 22, fontWeight: 700, margin: "0 0 4px", color: "#2C3E50" },
  propertyCityLine: { fontSize: 14, color: "#7F8C8D", margin: "0 0 16px" },
  infoRow: { display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" },
  infoChip: { display: "flex", flexDirection: "column", alignItems: "center" },
  infoLabel: { fontSize: 11, color: "#95A5A6", textTransform: "uppercase", letterSpacing: "0.04em" },
  infoValue: { fontSize: 13, fontWeight: 600, color: "#2C3E50" },

  // Tab bar
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #DEE2E6",
    position: "sticky",
    top: 67,
    zIndex: 99,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch"
  },
  tabBarInner: {
    maxWidth: 960,
    margin: "0 auto",
    display: "flex",
    padding: "0 12px"
  },
  tab: {
    flex: 1,
    padding: "14px 16px",
    border: "none",
    borderBottom: "3px solid transparent",
    background: "none",
    color: "#7F8C8D",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    textAlign: "center",
    whiteSpace: "nowrap"
  },
  tabActive: { color: "#1B4F72", borderBottomColor: "#1B4F72" },

  // Main content
  mainContent: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "20px 16px 40px"
  },

  // Section
  section: {
    background: "#fff",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    overflow: "hidden"
  },
  sectionTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "16px 20px",
    border: "none",
    borderBottom: "3px solid #1B4F72",
    background: "none",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left"
  },
  collapseIcon: { fontSize: 12, transition: "transform 0.2s" },

  // Items
  itemCard: { padding: "12px 20px", borderBottom: "1px solid #F0F2F5" },
  itemHeader: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  itemName: { flex: 1, fontSize: 14, minWidth: 120 },
  badge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 4,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap"
  },
  itemNotes: { fontSize: 12, color: "#555", fontStyle: "italic", marginTop: 4, paddingLeft: 4 },

  // Photos
  photoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: 8,
    marginTop: 8
  },
  photoThumb: {
    cursor: "pointer",
    borderRadius: 6,
    overflow: "hidden",
    border: "1px solid #DEE2E6",
    transition: "transform 0.15s"
  },
  photoThumbImg: { width: "100%", height: 80, objectFit: "cover", display: "block" },
  photoCaption: { fontSize: 10, color: "#7F8C8D", padding: "3px 6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // Gallery
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 12
  },
  galleryItem: {
    cursor: "pointer",
    borderRadius: 10,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    transition: "transform 0.15s, box-shadow 0.15s"
  },
  galleryImg: { width: "100%", height: 140, objectFit: "cover", display: "block" },
  galleryCaption: { fontSize: 12, color: "#555", padding: "8px 10px" },
  photoSectionTitle: { fontSize: 16, fontWeight: 600, color: "#2C3E50", marginBottom: 12 },

  // Summary
  conditionBanner: { color: "#fff", padding: "20px 24px", borderRadius: 10, marginBottom: 20 },
  conditionLabel: { fontSize: 12, opacity: 0.9, marginBottom: 4 },
  conditionValue: { fontSize: 22, fontWeight: 700 },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 },
  statCard: { border: "1px solid #DEE2E6", borderRadius: 8, padding: 14, textAlign: "center", background: "#fff" },
  statValue: { fontSize: 26, fontWeight: 700 },
  statLabel: { fontSize: 11, color: "#7F8C8D", marginTop: 4 },
  passRateSection: { marginBottom: 20 },
  passRateLabel: { fontSize: 14, fontWeight: 600, marginBottom: 6 },
  progressBar: { height: 14, background: "#E8E8E8", borderRadius: 7, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 7, transition: "width 0.5s" },
  summarySubtitle: { fontSize: 15, fontWeight: 600, color: "#2C3E50", marginBottom: 10 },
  summaryText: { fontSize: 14, lineHeight: 1.6, color: "#2C3E50" },
  severityRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  sevDot: { width: 12, height: 12, borderRadius: 2, flexShrink: 0 },
  sevLabel: { fontSize: 13, fontWeight: 600, width: 80 },
  sevCount: { fontSize: 13, width: 24, textAlign: "right" },
  sevBar: { flex: 1, height: 12, background: "#E8E8E8", borderRadius: 6, overflow: "hidden" },
  sevBarFill: { height: "100%", borderRadius: 6 },
  deficiencyCard: { padding: 14, borderRadius: 8, marginBottom: 10, borderLeft: "4px solid #E74C3C" },
  defHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  defName: { fontSize: 14, fontWeight: 600, flex: 1 },
  defSection: { fontSize: 12, color: "#7F8C8D", marginBottom: 2 },
  defNotes: { fontSize: 13, color: "#2C3E50", fontStyle: "italic", marginTop: 6 },

  // Sign
  signSection: { background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  signTitle: { fontSize: 20, fontWeight: 700, color: "#2C3E50", marginBottom: 4 },
  signSubtitle: { fontSize: 14, color: "#7F8C8D", marginBottom: 24, lineHeight: 1.4 },
  signField: { marginBottom: 20 },
  signLabel: { display: "block", fontSize: 13, fontWeight: 500, color: "#6e6e73", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  signInput: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #d2d2d7",
    borderRadius: 10,
    fontSize: 16,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    WebkitAppearance: "none"
  },
  canvasWrapper: { position: "relative", border: "1px solid #d2d2d7", borderRadius: 10, overflow: "hidden", background: "#fafafa" },
  canvas: { display: "block", width: "100%", height: "auto", touchAction: "none", cursor: "crosshair" },
  canvasPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#c7c7cc",
    fontSize: 16,
    pointerEvents: "none",
    fontStyle: "italic"
  },
  clearBtn: {
    marginTop: 6,
    padding: "6px 14px",
    borderRadius: 8,
    border: "none",
    background: "#f5f5f7",
    color: "#6e6e73",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 500
  },
  submitBtn: {
    width: "100%",
    padding: "14px 24px",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
    fontFamily: "inherit"
  },
  errorText: { color: "#E74C3C", fontSize: 13, marginBottom: 8 },
  signedContainer: { textAlign: "center", padding: "48px 20px", background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  signedCheck: { width: 56, height: 56, borderRadius: "50%", background: "#27AE60", color: "#fff", fontSize: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 },
  signedTitle: { fontSize: 20, fontWeight: 700, color: "#2C3E50", marginBottom: 8 },
  signedInfo: { fontSize: 14, color: "#7F8C8D", marginBottom: 16 },
  signaturePreview: { maxWidth: 250, border: "1px solid #DEE2E6", borderRadius: 8, marginTop: 8 },

  // Lightbox
  lightbox: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 20
  },
  lightboxClose: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: 28,
    cursor: "pointer",
    padding: 8
  },
  lightboxImg: { maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 6, cursor: "default" },
  lightboxCaption: { color: "#fff", fontSize: 14, marginTop: 12, textAlign: "center" },

  // Misc
  emptyState: { textAlign: "center", padding: "60px 20px", color: "#95A5A6", fontSize: 15 },
  screenCenter: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 20 },
  spinner: {
    width: 40,
    height: 40,
    border: "3px solid #E8E8E8",
    borderTopColor: "#1B4F72",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },

  // Footer
  footer: { background: "#fff", borderTop: "1px solid #DEE2E6", padding: "24px 20px", marginTop: 40 },
  footerInner: { maxWidth: 960, margin: "0 auto", textAlign: "center" },
  footerContact: { fontSize: 13, color: "#555", marginBottom: 12 },
  footerDivider: { color: "#DEE2E6" },
  footerLink: { color: "#555", textDecoration: "none" },
  footerDisclaimer: { fontSize: 11, color: "#95A5A6", lineHeight: 1.5, maxWidth: 600, margin: "0 auto 12px" },
  footerBrand: { fontSize: 11, color: "#C7C7CC" }
};

export default ClientPortalPage;
